import * as React from 'react';
import { useState } from 'react';

import { IAddress } from '@shared/types/address';

import { NumberFormControl } from '@admin/components/fields';
import { NoDetailAddressInput } from '@admin/schema';
import { Button } from '@admin/styled/button';
import { DetailGrid } from '@admin/styled/detail_grid';
import { DetailItem } from '@admin/styled/detail_item';
import { Field } from '@admin/styled/field';
import { FieldHint } from '@admin/styled/field_hint';
import { FieldInput } from '@admin/styled/field_input';
import { FieldLabel } from '@admin/styled/field_label';

import { AutocompleteFormGroup } from './autocomplete_form_group';

const AddressTextField: React.FC<{
  id: string;
  label: string;
  disabled?: boolean;
  value: string;
  onChange(value: string): void;
}> = ({ id, label, disabled, value, onChange }) => (
  <Field>
    <FieldLabel htmlFor={id}>{label}</FieldLabel>
    <FieldInput
      id={id}
      type="text"
      disabled={disabled}
      value={value}
      onChange={(event) => onChange(event.target.value)}
    />
  </Field>
);

export const AddressForm: React.FC<{
  address: IAddress | NoDetailAddressInput;
  disabled?: boolean;
  onChange(address: NoDetailAddressInput): void;
}> = ({ onChange, address, disabled }) => {
  const [query, setQuery] = useState<string | undefined>();
  const [manualStreetEntry, setManualStreetEntry] = useState(() => Boolean(address.street?.trim()));
  const { aptsuite, street, city, state, country, zip, latitude, longitude } = address;

  const hasStreet = Boolean(street?.trim());
  const showPlacesSearch = !disabled && !hasStreet && !manualStreetEntry;

  const usePlacesSearchAgain = () => {
    setManualStreetEntry(false);
    setQuery(undefined);
    onChange({
      ...address,
      street: '',
      aptsuite: '',
      city: '',
      state: '',
      zip: '',
      latitude: null,
      longitude: null,
    });
  };

  return (
    <>
      {showPlacesSearch ? (
        <>
          <AutocompleteFormGroup
            label="Street"
            disabled={false}
            query={query}
            onQuery={setQuery}
            onChange={(place) => {
              setQuery(undefined);
              setManualStreetEntry(false);
              onChange({
                ...address,
                ...place,
              });
            }}
          />
          <FieldHint>
            <Button variant="link" type="button" onClick={() => setManualStreetEntry(true)}>
              Enter street manually
            </Button>
          </FieldHint>
        </>
      ) : (
        <>
          <AddressTextField
            id="address-street"
            label="Street"
            disabled={disabled}
            value={street ?? ''}
            onChange={(value) => {
              if (!value.trim()) {
                setManualStreetEntry(false);
              }
              onChange({
                ...address,
                street: value,
                latitude: null,
                longitude: null,
              });
            }}
          />
          {!disabled && (
            <FieldHint>
              <Button variant="link" type="button" onClick={usePlacesSearchAgain}>
                Search for address
              </Button>
            </FieldHint>
          )}
        </>
      )}

      <AddressTextField
        id="address-aptsuite"
        label="Apt / suite"
        disabled={disabled}
        value={aptsuite ?? ''}
        onChange={(value) => onChange({ ...address, aptsuite: value })}
      />
      <AddressTextField
        id="address-city"
        label="City"
        disabled={disabled}
        value={city ?? ''}
        onChange={(value) => onChange({ ...address, city: value })}
      />
      <AddressTextField
        id="address-state"
        label="State"
        disabled={disabled}
        value={state ?? ''}
        onChange={(value) => onChange({ ...address, state: value })}
      />
      <AddressTextField
        id="address-country"
        label="Country"
        disabled={disabled}
        value={country ?? ''}
        onChange={(value) => onChange({ ...address, country: value })}
      />
      <AddressTextField
        id="address-zip"
        label="ZIP"
        disabled={disabled}
        value={zip ?? ''}
        onChange={(value) => onChange({ ...address, zip: value })}
      />

      <DetailGrid>
        <DetailItem>
          <Field>
            <FieldLabel htmlFor="address-latitude">Latitude</FieldLabel>
            <NumberFormControl
              disabled={disabled}
              id="address-latitude"
              value={latitude ?? undefined}
              onChange={(value) => onChange({ ...address, latitude: value ?? null })}
            />
          </Field>
        </DetailItem>
        <DetailItem>
          <Field>
            <FieldLabel htmlFor="address-longitude">Longitude</FieldLabel>
            <NumberFormControl
              disabled={disabled}
              id="address-longitude"
              value={longitude ?? undefined}
              onChange={(value) => onChange({ ...address, longitude: value ?? null })}
            />
          </Field>
        </DetailItem>
      </DetailGrid>
    </>
  );
};
